import { rejectIfStepUp } from "../stepUp.ts";
import {
  ControlError,
  parseAgentVersions,
  parseDeleteDevice,
  parseDepartments,
  parseDevice,
  parseDevicePage,
  parseOrganisations,
  parseProblem,
  parseAuditLog,
  parseRecordingAccess,
  parseRecordingPage,
  parseRecordings,
  parseSession,
  parseQuality,
  type AgentVersions,
  type DeleteDeviceResult,
  type Department,
  type Device,
  type DevicePage,
  type Organisation,
  type AuditLogPage,
  type Recording,
  type RecordingAccessPage,
  type RecordingPage,
  type Session,
  type SessionQuality,
} from "./parse.ts";
import type { components } from "./schema.ts";
import { parseInstallerDisposition, type InstallerFileName } from "./installerSaveName.ts";
import { recordingSaveName, rememberRecordingName } from "./recordingName.ts";
import { noteServerDate } from "../desk/consentClock.ts";

export type CreateSessionRequest = components["schemas"]["CreateSessionRequest"];
export type EndSessionRequest = components["schemas"]["EndSessionRequest"];
export type RecordingIntent = components["schemas"]["RecordingIntent"];
export type SessionEvent = components["schemas"]["SessionEvent"];

export type AgentInstallerFile = {
  readonly fileName: InstallerFileName;
  readonly bytes: Blob;
};

export type AuditEventQuery = {
  cursor?: string;
  type?: string;
  from?: string;
  to?: string;
  deviceId?: string;
  sessionId?: string;
  limit?: number;
};

export type ControlClient = {
  listDevices(cursor?: string, tag?: string, limit?: number): Promise<DevicePage>;
  approveDevice(deviceId: string): Promise<Device>;
  renameDevice(deviceId: string, name: string): Promise<Device>;
  deleteDevice?(deviceId: string): Promise<DeleteDeviceResult>;
  downloadAgentInstaller(): Promise<AgentInstallerFile>;
  requestAdminConsole(deviceId: string): Promise<{ status: "queued" }>;
  listOrganisations(): Promise<Organisation[]>;
  listDepartments(organisationId: string): Promise<Department[]>;
  createOrganisation(name: string): Promise<Organisation>;
  createDepartment(organisationId: string, name: string): Promise<Department>;
  replaceDeviceTags(deviceId: string, tags: { key: string; value: string }[]): Promise<Device>;
  agentVersions(): Promise<AgentVersions>;
  createSession(body: CreateSessionRequest): Promise<Session>;
  getSession(sessionId: string): Promise<Session>;
  getQuality?(sessionId: string): Promise<SessionQuality>;
  endSession(sessionId: string, body: EndSessionRequest, options?: { keepalive?: boolean }): Promise<Session>;
  listRecordings(sessionId: string): Promise<Recording[]>;
  listCatalogue(cursor?: string): Promise<RecordingPage>;
  listRecordingAccess(cursor?: string): Promise<RecordingAccessPage>;
  listAuditEvents(query?: string | AuditEventQuery, signal?: AbortSignal): Promise<AuditLogPage>;
  placeDevice(deviceId: string, organisationId: string, departmentId: string): Promise<Device>;
  recordingContent(recordingId: string, intent: RecordingIntent): Promise<Blob>;
  reportSessionEvent(sessionId: string, body: SessionEvent): Promise<void>;
};

async function readBody(response: Response): Promise<unknown> {
  const textBody = await response.text();
  if (textBody.length === 0) return undefined;
  try {
    return JSON.parse(textBody) as unknown;
  } catch {
    return undefined;
  }
}

function isQueued(value: unknown): boolean {
  return typeof value === "object" && value !== null && "status" in value && value.status === "queued";
}

export function createControlClient(input: {
  apiBase: string;
  token: () => Promise<string>;
  fetchImpl?: typeof fetch;
}): ControlClient {
  const fetchImpl = input.fetchImpl ?? fetch;
  let tokenRequest: Promise<string> | null = null;

  function takeToken(): Promise<string> {
    if (tokenRequest) return tokenRequest;
    const request = input.token().finally(() => {
      if (tokenRequest === request) tokenRequest = null;
    });
    tokenRequest = request;
    return request;
  }

  async function send(method: string, path: string, body?: unknown, keepalive = false, signal?: AbortSignal): Promise<Response> {
    const token = await takeToken();
    const headers = new Headers({ Authorization: `Bearer ${token}` });
    if (body !== undefined) headers.set("Content-Type", "application/json");
    const response = await fetchImpl(`${input.apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      ...(keepalive ? { keepalive: true } : {}),
      signal,
    });
    noteServerDate(Date.now(), response.headers.get("date"));
    return response;
  }

  async function json(method: string, path: string, body?: unknown, keepalive = false, signal?: AbortSignal): Promise<unknown> {
    const response = await send(method, path, body, keepalive, signal);
    await rejectIfStepUp(response);
    const payload = await readBody(response);
    if (!response.ok) throw new ControlError(parseProblem(payload, response.status));
    return payload;
  }

  return {
    async listDevices(cursor, tag, limit) {
      return parseDevicePage(await json("GET", inventoryPath(cursor, tag, limit)));
    },
    async approveDevice(deviceId) {
      return parseDevice(await json("POST", `/v1/inventory/devices/${deviceId}/approve`));
    },
    async renameDevice(deviceId, name) {
      return parseDevice(await json("PUT", `/v1/inventory/devices/${deviceId}/name`, { name }));
    },
    async deleteDevice(deviceId) {
      return parseDeleteDevice(await json("POST", `/v1/devices/${deviceId}/delete`));
    },
    async placeDevice(deviceId, organisationId, departmentId) {
      return parseDevice(
        await json("PUT", `/v1/inventory/devices/${deviceId}/placement`, { organisationId, departmentId }),
      );
    },
    async listOrganisations() {
      return parseOrganisations(await json("GET", "/v1/organisations"));
    },
    async listDepartments(organisationId) {
      return parseDepartments(await json("GET", `/v1/organisations/${organisationId}/departments`));
    },
    async createOrganisation(name) {
      const rows = parseOrganisations([await json("POST", "/v1/organisations", { name })]);
      return rows[0]!;
    },
    async createDepartment(organisationId, name) {
      const rows = parseDepartments([await json("POST", `/v1/organisations/${organisationId}/departments`, { name })]);
      return rows[0]!;
    },
    async replaceDeviceTags(deviceId, tags) {
      return parseDevice(await json("PUT", `/v1/inventory/devices/${deviceId}/tags`, { tags }));
    },
    async agentVersions() {
      return parseAgentVersions(await json("GET", "/v1/agent/versions"));
    },
    async createSession(body) {
      return parseSession(await json("POST", "/v1/sessions", body));
    },
    async getSession(sessionId) {
      return parseSession(await json("GET", `/v1/sessions/${sessionId}`));
    },
    async getQuality(sessionId) {
      return parseQuality(await json("GET", `/v1/sessions/${sessionId}/quality`));
    },
    async endSession(sessionId, body, options) {
      return parseSession(await json("POST", `/v1/sessions/${sessionId}/end`, body, options?.keepalive === true));
    },
    async listRecordings(sessionId) {
      return parseRecordings(await json("GET", `/v1/sessions/${sessionId}/recordings`));
    },
    async listCatalogue(cursor) {
      const params = new URLSearchParams();
      if (cursor) params.set("cursor", cursor);
      const query = params.toString();
      return parseRecordingPage(await json("GET", query.length === 0 ? "/v1/recordings" : `/v1/recordings?${query}`));
    },
    async listRecordingAccess(cursor) {
      const params = new URLSearchParams({ type: "RecordingAccessed" });
      if (cursor) params.set("cursor", cursor);
      return parseRecordingAccess(await json("GET", `/v1/audit/events?${params.toString()}`));
    },
    async listAuditEvents(query, signal) {
      const params = new URLSearchParams();
      if (typeof query === "string") {
        if (query.length > 0) params.set("cursor", query);
      } else if (query) {
        if (query.cursor) params.set("cursor", query.cursor);
        if (query.type) params.set("type", query.type);
        if (query.from) params.set("from", query.from);
        if (query.to) params.set("to", query.to);
        if (query.deviceId) params.set("deviceId", query.deviceId);
        if (query.sessionId) params.set("sessionId", query.sessionId);
        if (query.limit) params.set("limit", String(query.limit));
      }
      const text = params.toString();
      return parseAuditLog(await json("GET", text.length === 0 ? "/v1/audit/events" : `/v1/audit/events?${text}`, undefined, false, signal));
    },
    async recordingContent(recordingId, intent) {
      if (intent !== "Play" && intent !== "Download") {
        throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid recording intent", status: 400 });
      }
      const path = `/v1/recordings/${recordingId}/content?intent=${intent}`;
      if (/blob\.core\.windows\.net/i.test(path) || /[?&]sig=/i.test(path)) {
        throw new ControlError({ type: "urn:wylo-rc:error:forbidden", title: "Storage URLs are not playback sources", status: 400 });
      }
      const response = await send("GET", path);
      await rejectIfStepUp(response);
      if (!response.ok) throw new ControlError(parseProblem(await readBody(response), response.status));
      const blob = await response.blob();
      const save = recordingSaveName(response.headers.get("Content-Disposition"));
      if (save) rememberRecordingName(blob, save);
      return blob;
    },
    async reportSessionEvent(sessionId, body) {
      await json("POST", `/v1/sessions/${sessionId}/events`, body);
    },
    async requestAdminConsole(deviceId) {
      const body = await json("POST", `/v1/devices/${deviceId}/admin-console`);
      if (!isQueued(body)) {
        throw new ControlError({ type: "urn:wylo-rc:error:validation", title: "Invalid admin console response", status: 502 });
      }
      return { status: "queued" as const };
    },
    async downloadAgentInstaller() {
      const target = `${input.apiBase}/v1/agent/installer`;
      if (forbiddenInstallerHost(target)) {
        throw installerBlocked();
      }
      const token = await input.token();
      const headers = new Headers({ Authorization: `Bearer ${token}` });
      const response = await fetchImpl(target, { method: "GET", headers, redirect: "manual" });
      if (response.type === "opaqueredirect" || (response.status >= 300 && response.status < 400)) {
        throw installerBlocked();
      }
      if (response.url && forbiddenInstallerHost(response.url)) {
        throw installerBlocked();
      }
      await rejectIfStepUp(response);
      if (!response.ok) throw new ControlError(parseProblem(await readBody(response), response.status));
      const fileName = parseInstallerDisposition(response.headers.get("Content-Disposition"));
      return { fileName, bytes: await response.blob() };
    },
  };
}

export function idleHierarchy(): Pick<ControlClient, "createOrganisation" | "createDepartment" | "replaceDeviceTags" | "agentVersions"> {
  return {
    async createOrganisation(name) {
      return { organisationId: "00000000-0000-4000-8000-00000000c001", name };
    },
    async createDepartment(organisationId, name) {
      return { departmentId: "00000000-0000-4000-8000-00000000c002", organisationId, name };
    },
    async replaceDeviceTags() {
      throw new Error("tags are not available in this fixture");
    },
    async agentVersions() {
      return {
        windows: { published: false, version: null },
        macos: { published: false, version: null },
      };
    },
  };
}

export function idleRecordingPages(): Pick<
  ControlClient,
  "listCatalogue" | "listRecordingAccess" | "listAuditEvents" | "placeDevice" | "createOrganisation" | "createDepartment" | "replaceDeviceTags" | "agentVersions"
> {
  return {
    ...idleHierarchy(),
    async listCatalogue() {
      return { items: [] };
    },
    async listRecordingAccess() {
      return { items: [], nextCursor: null };
    },
    async listAuditEvents() {
      return { items: [], nextCursor: null };
    },
    async placeDevice() {
      throw new Error("placement is not available in this fixture");
    },
  };
}

function inventoryPath(cursor?: string, tag?: string, limit?: number): string {
  const params = new URLSearchParams();
  if (tag) params.set("tag", tag);
  if (cursor) params.set("cursor", cursor);
  if (typeof limit === "number") params.set("limit", String(limit));
  const text = params.toString();
  return text.length === 0 ? "/v1/inventory/devices" : `/v1/inventory/devices?${text}`;
}

function forbiddenInstallerHost(url: string): boolean {
  return /blob\.core\.windows\.net/i.test(url) || /github\.com/i.test(url);
}

function installerBlocked(): ControlError {
  return new ControlError({
    type: "urn:wylo-rc:error:agent-installer",
    title: "Could not download the agent",
    status: 502,
  });
}
